import { type QueryFunction, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import axiosInstance from '../../../constants/axiosInstance';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';

interface Asset {
  _id: string;
  plantName: string;
}

export interface DataLogger {
  _id: string;
  name: string;
  serialNumber: string;
  createdBy: string;
  version: number;
  debug: boolean;
  plcBrand: string;
  pollingInterval: number;
  plcModel: string;
  assetId: string;
  asset?: Asset | Asset[];
  lastValueTime?: string;
  modbusHost: string;
  modbusPort: number;
  unitIdReal: number;
  startAddressReal: number;
  registerCountReal: number;
  unitIdBool: number;
  startAddressBool: number;
  registerCountBool: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface DataLoggerResponse {
  status: boolean;
  data: {
    totalPages: number;
    currentPage: number;
    dataLoggers: DataLogger[];
  };
}

interface FetchResponse {
  status: boolean;
  totalPages: number;
  currentPage: number;
  dataLoggers: DataLogger[];
}

const url = (page: number, name: string, limit = 20): string =>
  `/data-logger/getDataLoggers?search=${name}&pageNumber=${page}&limit=${limit}`;

const fetchDataLoggers: QueryFunction<FetchResponse, [string, string], number> = async ({
  pageParam = 1,
  queryKey,
}) => {
  const [_key, searchString] = queryKey;

  const { data } = await axiosInstance().post<DataLoggerResponse>(url(pageParam, searchString));

  return {
    status: data.status,
    totalPages: data.data.totalPages,
    currentPage: data.data.currentPage,
    dataLoggers: data.data.dataLoggers,
  };
};

const nextPageParam = (lastPage: FetchResponse) => {
  if (lastPage.currentPage < lastPage.totalPages) {
    return lastPage.currentPage + 1;
  }
  return undefined;
};

export const useDataLoggerTable = (searchString: string) => {
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const { data, error, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useSuspenseInfiniteQuery({
      queryKey: ['dataLoggers', searchString] as [string, string],
      queryFn: fetchDataLoggers,
      getNextPageParam: nextPageParam,
      initialPageParam: 1,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const dataLoggers = useMemo(() => {
    return (data?.pages.flatMap(page => page.dataLoggers) ?? []) as DataLogger[];
  }, [data]);

  useIntersectionObserver(
    tableContainerRef,
    lastElementRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );

  return {
    dataLoggers,
    lastElementRef,
    tableContainerRef,
    error,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
}; 